import { Banknote, ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  METHODE_PAIEMENT_LABELS,
  ORIGINE_ENCAISSEMENT_LABELS,
  ecartCaisse,
} from '@sirajoptic/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, StatCard } from '@/components/ui/card';
import { InputField } from '@/components/ui/field';
import { ChargementBloc, Encart, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { TBody, TD, TH, THead, TR, Table } from '@/components/ui/table';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { aujourdhui, date as formatDate, dh, entier } from '@/lib/format';
import { useCaisse } from './api/caisse.api';

/** Décale une date ISO de n jours, sans passer par le fuseau local. */
function decaler(iso: string, jours: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + jours);
  return d.toISOString().slice(0, 10);
}

function heure(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Clôture de caisse.
 *
 * Ce qu'on fait le soir : on compte le tiroir, on le compare aux espèces
 * encaissées, et on imprime la feuille. Les autres méthodes de règlement se
 * rapprochent ailleurs (relevé bancaire, remise de chèques) — elles figurent
 * pour mémoire, mais seules les espèces se comptent à la main.
 *
 * Rien n'est enregistré : la clôture est une lecture de la journée. Le total
 * réimprimé demain tiendra compte d'une correction faite entre-temps, ce qui
 * vaut mieux qu'un chiffre figé que plus rien ne justifie.
 */
export function CaissePage() {
  const [jour, setJour] = useState(aujourdhui);
  const [compte, setCompte] = useState('');
  const { data, isPending, error, refetch, isFetching } = useCaisse(jour);

  const estAujourdhui = jour === aujourdhui();

  const ecart = useMemo(() => {
    if (compte.trim() === '' || !data) return null;
    const n = Number(compte.replace(',', '.'));
    return Number.isFinite(n) ? ecartCaisse(n, data.especes) : null;
  }, [compte, data]);

  if (isPending) return <ChargementBloc libelle="Chargement de la caisse…" />;
  if (error) return <EtatErreur erreur={error} onReessayer={() => void refetch()} />;

  return (
    <>
      <PageHeader
        titre="Clôture de caisse"
        description={`Encaissements du ${formatDate(data.date)} — ventes au comptoir et paiements sur commandes.`}
        actions={
          <>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Jour précédent"
                onClick={() => setJour((j) => decaler(j, -1))}
              >
                <ChevronLeft aria-hidden className="h-4 w-4" />
              </Button>
              <input
                type="date"
                value={jour}
                max={aujourdhui()}
                onChange={(e) => e.target.value && setJour(e.target.value)}
                aria-label="Jour à clôturer"
                className="rounded-md border border-line px-2 py-1.5 text-sm"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Jour suivant"
                disabled={estAujourdhui}
                onClick={() => setJour((j) => decaler(j, 1))}
              >
                <ChevronRight aria-hidden className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="primary"
              icone={<Printer aria-hidden className="h-4 w-4" />}
              onClick={() => window.print()}
            >
              Imprimer
            </Button>
          </>
        }
      />

      <PageBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            libelle="Espèces à compter"
            valeur={dh(data.especes)}
            accent
            icone={<Banknote aria-hidden className="h-4 w-4" />}
            secondaire="Ce qui doit être dans le tiroir"
          />
          <StatCard libelle="Total encaissé" valeur={dh(data.total)} secondaire="Toutes méthodes" />
          <StatCard libelle="Opérations" valeur={entier(data.operations)} />
          <StatCard
            libelle="Journée"
            valeur={formatDate(data.date)}
            secondaire={isFetching ? 'Mise à jour…' : estAujourdhui ? 'En cours' : 'Close'}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
          <Card>
            <CardHeader
              titre="Par méthode de règlement"
              description="Un remboursement compte en négatif et diminue le poste concerné."
            />
            <Table>
              <THead>
                <TR>
                  <TH>Méthode</TH>
                  <TH numerique>Opérations</TH>
                  <TH numerique>Montant</TH>
                </TR>
              </THead>
              <TBody>
                {data.par_methode.map((m) => (
                  <TR key={m.methode ?? 'sans'}>
                    <TD className="font-medium">
                      {m.methode ? METHODE_PAIEMENT_LABELS[m.methode] : 'Sans méthode indiquée'}
                    </TD>
                    <TD numerique className="text-ink-muted">
                      {entier(m.operations)}
                    </TD>
                    <TD numerique className="font-semibold">
                      {dh(m.montant)}
                    </TD>
                  </TR>
                ))}
                <TR>
                  <TD className="font-semibold">Total</TD>
                  <TD numerique className="text-ink-muted">
                    {entier(data.operations)}
                  </TD>
                  <TD numerique className="font-semibold text-mustard-600">
                    {dh(data.total)}
                  </TD>
                </TR>
              </TBody>
            </Table>
          </Card>

          {/* Le comptage du tiroir : une aide au calcul, pas une écriture. */}
          <Card className="sans-impression h-fit">
            <CardHeader
              titre="Comptage du tiroir"
              description="Saisissez ce que vous avez compté en espèces."
            />
            <div className="space-y-3 p-4">
              <InputField
                label="Espèces comptées (DH)"
                inputMode="decimal"
                value={compte}
                onChange={(e) => setCompte(e.target.value)}
                aide="Rien n'est enregistré : le calcul reste sur cet écran."
              />

              {ecart === null ? (
                <p className="text-sm text-ink-muted">
                  Attendu en caisse : <strong className="tabular text-ink">{dh(data.especes)}</strong>
                </p>
              ) : ecart === 0 ? (
                <Encart ton="succes" titre="Caisse juste">
                  Le tiroir correspond exactement aux espèces encaissées.
                </Encart>
              ) : (
                <Encart
                  ton={ecart > 0 ? 'moutarde' : 'danger'}
                  titre={ecart > 0 ? 'Excédent de caisse' : 'Manque en caisse'}
                >
                  Écart de <strong className="tabular">{dh(Math.abs(ecart))}</strong>{' '}
                  {ecart > 0
                    ? 'de plus que prévu. Une vente réglée en espèces a peut-être été enregistrée en carte.'
                    : 'de moins que prévu. Vérifiez les ventes du jour et la monnaie rendue.'}
                </Encart>
              )}
            </div>
          </Card>
        </div>

        <Card className="mt-4">
          <CardHeader
            titre="Détail de la journée"
            description="Dans l'ordre d'enregistrement — c'est ici qu'on retrouve l'origine d'un écart."
          />
          {data.lignes.length === 0 ? (
            <EtatVide
              titre="Aucun encaissement ce jour-là"
              description="Ni vente au comptoir, ni paiement reçu sur une commande."
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Heure</TH>
                  <TH>Origine</TH>
                  <TH>Référence</TH>
                  <TH>Client</TH>
                  <TH>Méthode</TH>
                  <TH numerique>Montant</TH>
                </TR>
              </THead>
              <TBody>
                {data.lignes.map((l) => (
                  <TR key={`${l.origine}-${l.id}`}>
                    <TD className="whitespace-nowrap tabular text-ink-muted">{heure(l.heure)}</TD>
                    <TD>
                      <Badge ton={l.origine === 'COMPTOIR' ? 'moutarde' : 'neutre'}>
                        {ORIGINE_ENCAISSEMENT_LABELS[l.origine]}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap font-mono text-xs">
                      {l.origine === 'COMMANDE' ? (
                        <Link
                          to={`/commandes?q=${encodeURIComponent(l.reference ?? l.libelle)}`}
                          className="underline decoration-line hover:decoration-ink"
                        >
                          {l.reference ?? '—'}
                        </Link>
                      ) : (
                        (l.reference ?? '—')
                      )}
                    </TD>
                    <TD>{l.libelle}</TD>
                    <TD className="whitespace-nowrap text-ink-muted">
                      {l.methode ? METHODE_PAIEMENT_LABELS[l.methode] : '—'}
                    </TD>
                    <TD
                      numerique
                      className={l.montant < 0 ? 'font-semibold text-danger' : 'font-semibold'}
                    >
                      {dh(l.montant)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </PageBody>
    </>
  );
}
